<template>
  <div class="w-full flex flex-col gap-y-1">
    <div :class="direction" class="w-full flex gap-x-4 gap-y-1">
      <label v-if="label" :for="field.name" :class="labelClass" class="flex items-center gap-x-2">
        <div>
          <span> {{ props.label }}</span>
          <span v-if="isRequired" class="text-brand-red ps-1">*</span>
        </div>
        <HintTippy v-if="hint" :hint="hint"></HintTippy>
      </label>
      <div class="w-full flex flex-col gap-y-1">
        <div
          :class="[
            {
              'border-s-4 border-s-primary rounded-s-sm': isRequired && !isDisabled
            },
            { 'bg-brand-gray-250 ': isDisabled }
          ]"
          class="p-1 w-full flex items-center border border-brand-grayblue-300"
          ref="wrapperDomRef"
        >
          <tippy
            :on-click-outside="handleBlur"
            :arrow="false"
            theme="light-border"
            interactive
            placement="bottom-start"
            :trigger="isDisabled ? 'manual' : 'focus click'"
            class="w-full flex items-center"
            :offset="[-10, 10]"
            @mount="setTippyWidth"
          >
            <div class="w-full flex justify-between px-2 max-h-24">
              <div
                :disabled="isDisabled"
                class="overflow-auto flex flex-row flex-wrap gap-1 tippy__trigger w-full text-start outline-none text-brand-black-secondary"
                @click.prevent="handleClick"
              >
                <input
                  v-model="searchValue"
                  type="text"
                  class="w-full rounded-sm outline-none focus:border-primary"
                  ref="searchQueryDomRef"
                  @keydown="handleDelete($event)"
                  :placeholder="props.placeholder"
                />
                <div
                  v-for="selectedOption in selectedMutipleOptions"
                  :key="selectedOption.id"
                  class="flex items-center justify-between gap-y-1 border rounded ps-2 bg-primary text-white bg-gradient-to-tr from-linear-s to-linear-e w-full pl-2"
                >
                  <div class="pe-1">{{ selectedOption[optionValue] }}</div>
                  <div
                    class="h-full px-1 flex items-center cursor-pointer hover:bg-brand-grayblue-300 border-s"
                    @click.stop.prevent="removeMutipleOption(selectedOption)"
                  >
                    <div class="fa fa-close"></div>
                  </div>
                </div>
              </div>
            </div>
            <template #content="{ hide }">
              <div
                class="!overflow-hidden tippy-content__container w-full p-0.5"
                :style="{ width: wrapperWidth }"
              >
                <div class="w-full flex flex-col gap-y-1">
                  <div class="max-h-48 h-full overflow-y-auto">
                    <ul class="divide-y pb-2">
                      <li v-for="(option, key) in filteredOptions" :key="key" class="">
                        <button
                          class="ps-3 py-1.5 hover:bg-gray-100 w-full text-start"
                          @click.prevent="
                            () => {
                              onSelection(option)
                            }
                          "
                        >
                          {{ option[optionValue] }}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </template>
          </tippy>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import HintTippy from '@/components/Form/HintTyppy.vue'
import { useDebounceFn, useEventListener } from '@vueuse/core'
import Fuse from 'fuse.js'
import { useFieldArray } from 'vee-validate'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Tippy } from 'vue-tippy'

const emits = defineEmits(['onSelection', 'onBlur', 'onClick'])

const props = defineProps({
  field: Object,
  label: {
    type: String,
    required: true
  },
  isDisabled: [Boolean, Object],
  hint: String,
  placeholder: String,
  showRules: Object,

  options: {
    type: Array,
    required: true
  },
  rules: Object,
  direction: {
    type: String,
    default: 'flex-row md:items-center flex-col md:flex-row'
  },
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  optionValue: String
})

const searchValue = ref()
const searchQueryDomRef = ref()
const fuse = ref()
const selectOptions = ref()
const i18n = useI18n()
const wrapperDomRef = ref()
const wrapperWidth = ref()
const selectedMutipleOptions = ref([])
const selectedCodes = []

const setTippyWidth = (instance) => {
  nextTick(() => {
    if (instance) {
      document.getElementById(`tippy-${instance.id}`).getElementsByClassName('tippy-box')[0].style[
        'max-width'
      ] = wrapperDomRef.value.offsetWidth + 'px'
    }
  })
}
const handleDelete = (e) => {
  if (e.key === 'Backspace' && !searchValue.value) {
    const lastOption = selectedMutipleOptions.value[selectedMutipleOptions.value.length - 1]
    if (lastOption) {
      removeMutipleOption(lastOption)
    }
  }
}

const cleanupResize = useEventListener(
  window,
  'resize',
  useDebounceFn(() => {
    setTippyWidth()
  }, 1000)
)
onMounted(async () => {
  setTippyWidth()

  fuse.value = new Fuse(props.options, { includeScore: true, threshold: 0.2 })
})
onBeforeUnmount(() => {
  cleanupResize()
})

const { remove, push } = useFieldArray(props.field.code)

const filteredOptions = computed(() => {
  if (!searchValue.value) {
    return props.options.filter((x) => {
      return !selectedMutipleOptions.value.find((item) => item.code == x.code)
    })
  }
  const result = fuse.value.search(searchValue.value)
  return result
    .map((r) => r.item)
    .filter((x) => {
      return !selectedMutipleOptions.value.find((item) => item.code == x.code)
    })
})

const removeMutipleOption = (option) => {
  const idx = selectedMutipleOptions.value.findIndex((item) => item.code == option.code)
  remove(idx)

  selectedMutipleOptions.value = selectedMutipleOptions.value.filter(
    (item) => item.code !== option.code
  )
  selectOptions.value = [...props.options, option]
  fuse.value.add(option)
  searchValue.value = ''
  nextTick(() => {
    searchQueryDomRef.value.focus()
  })
  selectedCodes.value = selectedMutipleOptions.value.map((option) => option.code)
  emits('onSelection', selectedCodes.value)
}

const handleMutipleSelected = (option) => {
  push(option.id)
  selectOptions.value = props.options.filter((item) => item.code === option.code)
  const multiOptions = new Set(selectedMutipleOptions.value.map((item) => item.code))
  if (!multiOptions.has(option.code)) {
    multiOptions.add(option.code)
    selectedMutipleOptions.value = [...selectedMutipleOptions.value, option]
  }
  fuse.value.remove((item) => item.code === option.code)
  searchValue.value = ''
  nextTick(() => {
    searchQueryDomRef.value.focus()
  })
  selectedCodes.value = selectedMutipleOptions.value.map((option) => option.code)
  emits('onSelection', selectedCodes.value)
}

const onSelection = (option) => {
  handleMutipleSelected(option)
}

const handleBlur = () => {
  emits('onBlur')
}

const handleClick = () => {
  emits('onClick')
}


const isRequired = computed(() => !!props.rules?.find((r) => r.name == 'required' || 'required_ui'))
</script>

<style lang="scss" scoped></style>
